import React, { useEffect, useState } from 'react';
import { WifiOff, Download } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Full-page overlay shown when the browser goes offline.
 * Reminds the user that the streaming library needs an internet connection,
 * but their previously-downloaded recordings are still playable locally
 * (either in the browser's Downloads folder or wherever they saved them).
 *
 * A "See my downloads" button links to /download-center so recorded downloads
 * are one tap away.
 *
 * Auto-dismisses when the connection comes back.
 */
const OfflineBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onOffline = () => { setOffline(true); setDismissed(false); };
    const onOnline  = () => setOffline(false);
    window.addEventListener('offline', onOffline);
    window.addEventListener('online',  onOnline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online',  onOnline);
    };
  }, []);

  if (!offline || dismissed) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0f1729]/95 backdrop-blur-sm p-6"
      role="dialog"
      aria-modal="true"
      data-testid="offline-banner"
    >
      <div className="max-w-sm w-full bg-white rounded-2xl p-6 shadow-2xl border border-[#9a7b24]/40 text-center">
        <div className="w-14 h-14 rounded-full bg-[#9a7b24]/15 flex items-center justify-center mx-auto mb-4">
          <WifiOff className="w-7 h-7 text-[#9a7b24]" />
        </div>
        <h2 className="text-xl font-bold text-[#0f1729] mb-2">You&apos;re offline</h2>
        <p className="text-sm text-[#4b5563] mb-5 leading-relaxed">
          The Vaad Otzar library streams from the internet, so new shiurim aren&apos;t
          available right now — but any recordings you already downloaded still play
          straight from your device.
        </p>
        <button
          type="button"
          onClick={() => { setDismissed(true); if (location.pathname !== '/download-center') navigate('/download-center'); }}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#9a7b24] hover:bg-[#7d6119] text-white font-semibold py-2.5 px-5 transition-colors"
          data-testid="offline-open-downloads"
        >
          <Download className="w-4 h-4" /> See my downloads
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="mt-3 text-xs text-[#8a8f99] hover:text-[#0f1729]"
        >
          Continue browsing
        </button>
      </div>
    </div>
  );
};

export default OfflineBanner;
